import { convertBooleanToYesNo } from '@fruitful/fruitils';
import {
    CASH_BACK_POLICY_LABELS,
    type GuidanceGeneralSessionInfo,
    type GuidanceOnboardingSessionInfo,
    PREMIUM_CARD_POLICY_LABELS,
    type SubscriptionPhaseSummary,
    type SubscriptionProduct,
} from '@fruitful/types';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import capitalize from 'lodash/capitalize';
import { Badge } from 'primereact/badge';
import { Column } from 'primereact/column';
import {
    DataTable,
    type DataTableRowEditCompleteEvent,
} from 'primereact/datatable';
import React, { useState } from 'react';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    FootnoteEmphasized,
    H6Emphasized,
    SmoothieScrollView,
    type StackProps,
    StickyNoteIcon,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import { AddressTable } from '../../../address/components/AddressTable';
import {
    GuideChipRenderer,
    MonospaceDetailText,
    MonospaceNumericText,
} from '../../../common/components/GridCellRenderers';
import { Button } from '../../../common/components/GroveButtons';
import { PageContentContainer } from '../../../common/components/GroveContainers';
import { GroveSimpleModal } from '../../../common/components/GroveSimpleModal';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../../common/components/Toasts';
import type { BadgeSeverity } from '../../../common/types/Badge';
import {
    centsToFormattedDollars,
    formatTenure,
    formatToSimpleDateString,
    formatToSimpleDateTimeString,
} from '../../../common/utils/formatting';
import { GuideSelectionDropdown } from '../../../guides/components/GuideSelectionDropdown';
import { CopyableTextField } from '../../../member/components/renderers/CopyableTextField';
import { useStopMembershipCancellationAndRefetch } from '../../../membership/hooks/useStopMembershipCancellation';
import {
    adminUserAtom,
    allGuidesAtom,
    mutatedMembershipAtom,
} from '../../entityAtoms';
import {
    type GuideDetails,
    type MembershipDetails,
    type StripeSubscriptionSummary,
} from '../../entityTypes';
import { membershipGuidanceNotesModalVisibleAtom } from './MembershipGuidanceNotesModal';

interface MembershipInfoProps extends StackProps {
    membershipDetails: MembershipDetails | null;
}

export const MembershipInfo = ({
    membershipDetails,
    ...stackProps
}: MembershipInfoProps) => {
    const adminUser = useAtomValue(adminUserAtom);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [isNotesModalVisible, setIsNotesModalVisible] = useAtom(
        membershipGuidanceNotesModalVisibleAtom,
    );

    const canView = adminUser.authorization.selectedMembership.notes.canView;

    return (
        <PageContentContainer {...stackProps}>
            <YStack gap={16} width="100%">
                <XStack justifyContent="space-between" width="100%">
                    <H6Emphasized>Membership info</H6Emphasized>
                    <XStack gap={8}>
                        {canView && !isNotesModalVisible && (
                            <Button
                                variant="secondary"
                                size="small"
                                icon={StickyNoteIcon}
                                onPress={() => {
                                    setIsNotesModalVisible(true);
                                }}
                            >
                                Notes
                            </Button>
                        )}
                        <Button
                            variant="secondary"
                            size="small"
                            onPress={() => {
                                setIsDetailsModalOpen(true);
                            }}
                        >
                            View details
                        </Button>
                    </XStack>
                </XStack>
                <MembershipInfoTable membershipDetails={membershipDetails} />
            </YStack>
            <GroveSimpleModal
                isOpen={isDetailsModalOpen}
                onClose={() => {
                    setIsDetailsModalOpen(false);
                }}
                renderInAppRoot={false} // When this is true, the PrimeReact components are stacked below the modal.
            >
                <MembershipDetailsTable membershipDetails={membershipDetails} />
            </GroveSimpleModal>
        </PageContentContainer>
    );
};

const MembershipInfoTable = ({
    membershipDetails,
}: {
    membershipDetails: MembershipDetails | null;
}) => {
    if (membershipDetails == null) {
        return null;
    }

    const membershipData = [membershipDetails];

    return (
        <YStack gap={8}>
            <style type="text/css">
                {`
                    .vertical-align-top .p-datatable-tbody > tr > td {
                        vertical-align: top;
                    }
                `}
            </style>
            <DataTable
                value={membershipData}
                size="small"
                tableStyle={{ width: '100%' }}
                tableClassName="vertical-align-top"
            >
                <Column
                    field="createdAt"
                    header="Tenure"
                    body={MembershipTenureRenderer}
                />
                <Column
                    field="isMembershipActive"
                    header="Is Active?"
                    headerTooltip="Whether the member can access our platform - not directly tied to Stripe subscription"
                    headerTooltipOptions={{ position: 'top' }}
                    body={MembershipActiveRenderer}
                />
                <Column
                    field="subscription"
                    header="Subscription"
                    body={MembershipSubscriptionRenderer}
                />
                <Column
                    field="guideId"
                    header="Guide"
                    body={GuideChipRenderer}
                />
            </DataTable>
        </YStack>
    );
};

export const MembershipTenureRenderer = ({ createdAt }: MembershipDetails) => {
    const tenure = formatTenure(createdAt);

    return (
        <YStack gap={4}>
            <XStack gap={4} alignItems="center">
                <FootnoteEmphasized>Tenure:</FootnoteEmphasized>
                <BodyMediumEmphasized>{tenure.value}</BodyMediumEmphasized>
                {tenure.isNew && <Badge value="NEW" />}
            </XStack>
            <XStack gap={4} alignItems="center">
                <FootnoteEmphasized>Start date:</FootnoteEmphasized>
                <MonospaceDetailText>
                    {formatToSimpleDateString(createdAt)}
                </MonospaceDetailText>
            </XStack>
        </YStack>
    );
};

export const MembershipActiveRenderer = (
    membershipDetails: MembershipDetails,
) => {
    return <MembershipActiveCell {...membershipDetails} />;
};

const MembershipActiveCell = (membershipDetails: MembershipDetails) => {
    const { id, isMembershipActive, users, cancellationScheduledJobArn } =
        membershipDetails;
    const adminUser = useAtomValue(adminUserAtom);
    const toast = useToastController();
    const [isStoppingCancellation, setIsStoppingCancellation] = useState(false);
    const { stopMembershipCancellationAsync } =
        useStopMembershipCancellationAndRefetch(membershipDetails);

    const canStopCancellation =
        adminUser.authorization.selectedMembership.cancelMembership.canUse;
    const hasPendingCancellation = cancellationScheduledJobArn != null;

    const cancellationDate = users.find(
        (user) => user.cancellationDate !== null,
    )?.cancellationDate;

    let badgeValue = 'ACTIVE';
    let severity: BadgeSeverity = 'success';
    if (!isMembershipActive) {
        badgeValue = 'INACTIVE';
        severity = 'danger';
    } else if (cancellationDate != null) {
        badgeValue = 'CANCELLATION PENDING';
        severity = 'warning';
    }

    const handleStopCancellation = async () => {
        setIsStoppingCancellation(true);

        try {
            await stopMembershipCancellationAsync(id);
            toast.show(
                'Membership cancellation stopped',
                MutationSuccessToastOptions,
            );
        } catch {
            toast.show(
                'Error stopping membership cancellation',
                MutationErrorToastOptions(),
            );
        } finally {
            setIsStoppingCancellation(false);
        }
    };

    return (
        <YStack gap={4} alignItems="flex-start">
            <XStack gap={4}>
                <Badge value={badgeValue} severity={severity} />
            </XStack>
            {hasPendingCancellation && canStopCancellation && (
                <Button
                    variant="primary"
                    size="tiny"
                    tone="danger"
                    disabled={isStoppingCancellation}
                    onPress={() => {
                        void handleStopCancellation();
                    }}
                >
                    Stop Cancellation
                </Button>
            )}
        </YStack>
    );
};

const getStripeSubscriptionFriendlyStatus = (
    subscription: StripeSubscriptionSummary | null,
): string => {
    if (subscription == null) {
        return 'UNKNOWN';
    }

    if (subscription.status === 'trialing') {
        if (
            subscription.trialPrice === undefined ||
            subscription.trialPrice === 0
        ) {
            return 'FREE TRIAL';
        } else {
            return `$${subscription.trialPrice} TRIAL`;
        }
    } else {
        return subscription.status.toUpperCase();
    }
};

const getSubscriptionProductLabel = ({
    subscription,
}: Pick<MembershipDetails, 'subscription'>): string => {
    const displayName =
        subscription?.product?.displayName ?? subscription?.type ?? 'unknown';
    return displayName.toUpperCase();
};

const hasUpcomingSubscriptionPlanChange = (
    subscription: StripeSubscriptionSummary,
): boolean => {
    const { currentPhase, nextPhase } = subscription;
    return (
        nextPhase != null &&
        currentPhase != null &&
        (nextPhase.subscriptionProductId !==
            currentPhase.subscriptionProductId ||
            nextPhase.billingFrequency !== currentPhase.billingFrequency)
    );
};

export const MembershipSubscriptionRenderer = ({
    isMembershipActive,
    subscription,
}: MembershipDetails) => {
    const shouldShowCancelDate =
        subscription != null &&
        (subscription.cancelAtPeriodEnd || subscription.cancelAtDate != null);

    const productLabel = getSubscriptionProductLabel({
        subscription,
    });
    const isStripeSubscriptionActive =
        subscription?.status === 'active' ||
        subscription?.status === 'trialing';
    const stripeSubscriptionStatus =
        getStripeSubscriptionFriendlyStatus(subscription);
    const upcomingPlanChange =
        subscription != null && hasUpcomingSubscriptionPlanChange(subscription);

    return (
        <YStack gap={4}>
            <XStack gap={4} flexWrap="wrap">
                {subscription?.cancelAtPeriodEnd ? (
                    <Badge value="CANCELING" severity="danger" />
                ) : (
                    <Badge
                        value={stripeSubscriptionStatus}
                        severity={
                            stripeSubscriptionStatus === 'PAUSED'
                                ? 'warning'
                                : isStripeSubscriptionActive
                                  ? 'success'
                                  : 'danger'
                        }
                    />
                )}
                <Badge
                    value={productLabel}
                    severity={subscription != null ? 'info' : 'warning'}
                />
            </XStack>
            <XStack gap={4} alignItems="center">
                <FootnoteEmphasized>Billing:</FootnoteEmphasized>
                <MonospaceDetailText>
                    {capitalize(subscription?.billingFrequency) ?? 'Unknown'}{' '}
                    {/* {subscription?.product.billing?.hasAnnualCommitment
                        ? '(Annual commitment)'
                        : ''} */}
                </MonospaceDetailText>
            </XStack>
            {isMembershipActive && subscription != null && (
                <>
                    {shouldShowCancelDate ? (
                        <CancelDateInfo subscription={subscription} />
                    ) : (
                        <RenewalDateInfo subscription={subscription} />
                    )}
                </>
            )}
            {upcomingPlanChange && subscription.nextPhase != null && (
                <XStack gap={4} alignItems="center">
                    <FootnoteEmphasized>Next phase:</FootnoteEmphasized>
                    <MonospaceDetailText>
                        {subscription.nextPhase.product?.displayName ??
                            subscription.nextPhase.subscriptionProductId ??
                            'unknown'}{' '}
                        on{' '}
                        {formatToSimpleDateTimeString(
                            subscription.nextPhase.startDate,
                        )}
                    </MonospaceDetailText>
                </XStack>
            )}
        </YStack>
    );
};

const CancelDateInfo = ({
    subscription,
}: {
    subscription: StripeSubscriptionSummary;
}) => {
    return (
        <XStack gap={4} alignItems="center">
            {subscription.cancelAtPeriodEnd && (
                <FootnoteEmphasized>Cancel at period end</FootnoteEmphasized>
            )}
            <FootnoteEmphasized>Cancel date:</FootnoteEmphasized>
            <MonospaceDetailText>
                {subscription.cancelAtDate != null
                    ? formatToSimpleDateTimeString(subscription.cancelAtDate)
                    : subscription.renewalDate != null
                      ? `likely ${formatToSimpleDateTimeString(subscription.renewalDate)}`
                      : 'Unknown'}
            </MonospaceDetailText>
        </XStack>
    );
};
const RenewalDateInfo = ({
    subscription,
}: {
    subscription: StripeSubscriptionSummary;
}) => {
    return (
        <XStack gap={4} alignItems="center">
            <FootnoteEmphasized>Renewal date:</FootnoteEmphasized>
            <MonospaceDetailText>
                {formatToSimpleDateString(subscription.renewalDate)}
            </MonospaceDetailText>
        </XStack>
    );
};

const MembershipDetailsTable = ({
    membershipDetails,
}: {
    membershipDetails: MembershipDetails | null;
}) => {
    const adminUser = useAtomValue(adminUserAtom);
    const canEditMembership =
        adminUser.authorization.selectedMembership.general.canEdit;
    const allGuides = useAtomValue(allGuidesAtom);
    const setMutatedMembership = useSetAtom(mutatedMembershipAtom);

    if (membershipDetails == null) {
        return null;
    }

    const membershipData = [membershipDetails];
    const subscriptionData =
        membershipDetails.subscription != null
            ? [membershipDetails.subscription]
            : null;

    const userAddresses = membershipDetails.users.map((u) => u.userAddress);

    const onRowEditComplete = ({ newData }: DataTableRowEditCompleteEvent) => {
        const updatedMembership = newData as MembershipDetails;
        setMutatedMembership(updatedMembership);
    };

    return (
        <SmoothieScrollView
            containerStyle={{
                minWidth: '70vw',
                maxHeight: '70vh',
            }}
        >
            <YStack padding={24} gap={16}>
                <H6Emphasized>Membership details</H6Emphasized>
                <DataTable
                    value={membershipData}
                    size="small"
                    tableStyle={{ width: '100%', fontSize: 13 }}
                    editMode="row"
                    onRowEditComplete={onRowEditComplete}
                    scrollable
                >
                    <Column
                        field="id"
                        header="id"
                        body={({ id }: MembershipDetails) => {
                            return (
                                <CopyableTextField value={id}>
                                    {id}
                                </CopyableTextField>
                            );
                        }}
                    />
                    <Column
                        field="createdAt"
                        header="Created At"
                        body={({ createdAt }: MembershipDetails) => {
                            return formatToSimpleDateTimeString(createdAt);
                        }}
                    />
                    <Column
                        field="updatedAt"
                        header="Updated At"
                        body={({ updatedAt }: MembershipDetails) => {
                            return formatToSimpleDateTimeString(updatedAt);
                        }}
                    />
                    <Column
                        field="membershipType"
                        header="Member Count"
                        headerTooltip="Solo vs joint based on member count, not the Stripe plan"
                        headerTooltipOptions={{ position: 'top' }}
                        body={({ users }: MembershipDetails) => {
                            return users.length;
                        }}
                    />
                    <Column
                        field="product.displayName"
                        header="Subscribed to"
                        body={({
                            product,
                            stripeProductName,
                            subscription,
                        }: MembershipDetails) =>
                            product?.displayName ??
                            subscription?.product?.displayName ??
                            stripeProductName ??
                            'Unknown'
                        }
                    />
                    <Column field="isMembershipActive" header="Is Active?" />
                    <Column
                        field="guideId"
                        header="Guide"
                        body={({ guideId }: MembershipDetails) => {
                            if (guideId == null) {
                                return 'No guide';
                            }

                            return (
                                allGuides.find((guide) => guide.id === guideId)
                                    ?.fullName ?? '(Unknown)'
                            );
                        }}
                        editor={(options) =>
                            GuideSelectionDropdown({
                                options,
                                guides: allGuides,
                            })
                        }
                    />
                    <Column rowEditor hidden={!canEditMembership} />
                </DataTable>

                <H6Emphasized paddingTop={40}>
                    Subscription details
                </H6Emphasized>
                {subscriptionData != null ? (
                    <DataTable
                        value={subscriptionData}
                        size="small"
                        tableStyle={{ width: '100%', fontSize: 13 }}
                        scrollable
                    >
                        <Column field="status" header="Status" />

                        <Column
                            field="product.displayName"
                            header="Product"
                            body={({ product }: StripeSubscriptionSummary) =>
                                product?.displayName
                            }
                        />
                        <Column field="type" header="Type" />
                        <Column
                            field="hasPlusFeatures"
                            header="Has Plus Features?"
                            body={({ product }: StripeSubscriptionSummary) =>
                                convertBooleanToYesNo(product?.hasPlusFeatures)
                            }
                        />
                        <Column
                            field="billingFrequency"
                            header="Billing Frequency"
                        />
                        <Column
                            field="renewalDate"
                            header="Billing period end"
                            body={({
                                renewalDate,
                            }: StripeSubscriptionSummary) => {
                                return formatToSimpleDateTimeString(
                                    renewalDate,
                                );
                            }}
                        />
                        <Column
                            field="nextInvoiceAmountDueCents"
                            header="Next Invoice Amount"
                            body={SubscriptionRenewalAmountRenderer}
                        />
                        <Column field="promoCode" header="Promo Code" />
                        <Column
                            field="trialPrice"
                            header="1st Month Price ⓘ"
                            headerTooltip='The price of our "first month trial" when this member signed up'
                            headerTooltipOptions={{ position: 'top' }}
                            body={({
                                trialPrice,
                            }: StripeSubscriptionSummary) => {
                                return trialPrice === undefined ||
                                    trialPrice === 0
                                    ? 'n/a'
                                    : `$${trialPrice}`;
                            }}
                        />
                        <Column
                            field="cancelAtPeriodEnd"
                            header="Cancel At Period End?"
                        />
                        <Column
                            field="cancelAtDate"
                            header="Cancel At Date"
                            body={({
                                cancelAtDate,
                            }: StripeSubscriptionSummary) => {
                                return formatToSimpleDateTimeString(
                                    cancelAtDate,
                                );
                            }}
                        />
                        <Column field="trialVariant" header="Trial Variant" />
                    </DataTable>
                ) : (
                    <BodyMediumRegular>
                        Could not fetch subscription data.
                    </BodyMediumRegular>
                )}

                <SubscriptionProductDetails
                    product={
                        membershipDetails.subscription?.product ??
                        membershipDetails.product ??
                        null
                    }
                />
                {membershipDetails.subscription?.currentPhase != null && (
                    <SubscriptionPhaseDetails
                        subscription={membershipDetails.subscription}
                    />
                )}

                <AddressTable userAddresses={userAddresses} />

                {membershipDetails.guide != null && (
                    <>
                        <H6Emphasized paddingTop={40}>
                            Guide details
                        </H6Emphasized>
                        <DataTable
                            value={[membershipDetails.guide]}
                            size="small"
                            tableStyle={{ width: '100%', fontSize: 13 }}
                            scrollable
                        >
                            <Column
                                field="id"
                                header="id"
                                body={({ id }: GuideDetails) => {
                                    return (
                                        <CopyableTextField value={id}>
                                            {id}
                                        </CopyableTextField>
                                    );
                                }}
                            />
                            <Column field="firstName" header="First Name" />
                            <Column field="lastName" header="Last Name" />
                            <Column
                                field="email"
                                header="Email"
                                body={({ email }: GuideDetails) => {
                                    return (
                                        <CopyableTextField value={email}>
                                            {email}
                                        </CopyableTextField>
                                    );
                                }}
                            />
                            <Column field="status" header="Status" />
                        </DataTable>
                    </>
                )}
            </YStack>
        </SmoothieScrollView>
    );
};

const SubscriptionRenewalAmountRenderer = ({
    nextInvoiceAmountDueCents,
}: StripeSubscriptionSummary) => (
    <MonospaceNumericText>
        {nextInvoiceAmountDueCents != null
            ? centsToFormattedDollars(nextInvoiceAmountDueCents)
            : ''}
    </MonospaceNumericText>
);

const formatOnboardingSessions = (
    sessions: readonly GuidanceOnboardingSessionInfo[],
): string =>
    sessions.length === 0
        ? 'None'
        : sessions.map(({ displayName }) => displayName).join(', ');

const formatGeneralSessions = (
    sessions: readonly GuidanceGeneralSessionInfo[],
): string =>
    sessions.length === 0
        ? 'None'
        : sessions
              .map(({ displayName, annualLimit }) =>
                  annualLimit == null
                      ? `${displayName} (unlimited)`
                      : `${displayName} (${annualLimit}/yr)`,
              )
              .join(', ');

const SubscriptionProductDetails = ({
    product,
}: {
    product: SubscriptionProduct | null;
}) => {
    if (product == null) {
        return null;
    }

    return (
        <>
            <BodyMediumEmphasized paddingTop={10}>
                Subscription product
            </BodyMediumEmphasized>
            <DataTable
                value={[product]}
                size="small"
                tableStyle={{ width: '100%', fontSize: 13 }}
                scrollable
            >
                <Column field="displayName" header="Display Name" />
                <Column field="id" header="Product ID" />
                <Column
                    field="hasPlusFeatures"
                    header="Has Plus Features?"
                    body={({ hasPlusFeatures }: SubscriptionProduct) =>
                        convertBooleanToYesNo(hasPlusFeatures) ?? ''
                    }
                />
                <Column
                    field="guidance.includesAssignedGuide"
                    header="Assigned Guide?"
                    body={({ guidance }: SubscriptionProduct) =>
                        convertBooleanToYesNo(guidance.includesAssignedGuide) ??
                        ''
                    }
                />
                <Column
                    field="guidance.onboardingSessions"
                    header="Onboarding Sessions"
                    body={({ guidance }: SubscriptionProduct) =>
                        formatOnboardingSessions(guidance.onboardingSessions)
                    }
                />
                <Column
                    field="guidance.generalSessions"
                    header="General Session Allowance"
                    body={({ guidance }: SubscriptionProduct) =>
                        formatGeneralSessions(guidance.generalSessions)
                    }
                />
                <Column
                    field="banking.cashBackPolicy"
                    header="Cash Back Policy"
                    body={({ banking }: SubscriptionProduct) =>
                        CASH_BACK_POLICY_LABELS[banking.cashBackPolicy]
                    }
                />
                <Column
                    field="banking.premiumCardPolicy"
                    header="Premium Card Policy"
                    body={({ banking }: SubscriptionProduct) =>
                        PREMIUM_CARD_POLICY_LABELS[banking.premiumCardPolicy]
                    }
                />
                <Column field="banking.rateCashBack" header="Cash Back" />
                <Column field="banking.rateCashAPY" header="Cash APY" />
                <Column field="banking.rateInvestAUM" header="Invest AUM" />
                <Column
                    field="banking.thresholdCentsInvestAUM"
                    header="AUM Threshold"
                    body={({ banking }: SubscriptionProduct) => (
                        <MonospaceNumericText>
                            {centsToFormattedDollars(
                                banking.thresholdCentsInvestAUM,
                            )}
                        </MonospaceNumericText>
                    )}
                />
            </DataTable>
        </>
    );
};

interface SubscriptionPhaseRow extends SubscriptionPhaseSummary {
    label: string;
}

const SubscriptionPhaseDetails = ({
    subscription,
}: {
    subscription: StripeSubscriptionSummary;
}) => {
    const phases: SubscriptionPhaseRow[] = [
        ...(subscription.currentPhase != null
            ? [{ label: 'Current', ...subscription.currentPhase }]
            : []),
        ...(subscription.nextPhase != null
            ? [{ label: 'Next', ...subscription.nextPhase }]
            : []),
    ];

    return (
        <>
            <BodyMediumEmphasized paddingTop={10}>
                Subscription schedule
            </BodyMediumEmphasized>
            <DataTable
                value={phases}
                size="small"
                tableStyle={{ width: '100%', fontSize: 13 }}
                scrollable
            >
                <Column field="label" header="" />
                <Column
                    field="product.displayName"
                    header="Product"
                    body={({ product }: SubscriptionPhaseRow) =>
                        product?.displayName
                    }
                />
                <Column field="subscriptionProductId" header="Product ID" />
                <Column field="billingFrequency" header="Billing Frequency" />
                <Column
                    field="startDate"
                    header="Start"
                    body={({ startDate }: SubscriptionPhaseRow) =>
                        formatToSimpleDateTimeString(startDate)
                    }
                />
                <Column
                    field="endDate"
                    header="End"
                    body={({ endDate }: SubscriptionPhaseRow) =>
                        endDate != null
                            ? formatToSimpleDateTimeString(endDate)
                            : 'Ongoing'
                    }
                />
                <Column
                    field="billingAmountCents"
                    header="Amount"
                    body={({ billingAmountCents }: SubscriptionPhaseRow) => (
                        <MonospaceNumericText>
                            {billingAmountCents != null
                                ? centsToFormattedDollars(billingAmountCents)
                                : ''}
                        </MonospaceNumericText>
                    )}
                />
            </DataTable>
        </>
    );
};
